import {
  GLOBAL_SEARCH_BROWSE_LIMIT,
  GLOBAL_SEARCH_BROWSE_STATIC_TYPES,
  GLOBAL_SEARCH_STATIC_ENTRIES,
} from "@frontend-shared/constants";
import {
  EGlobalSearchGate,
  EGlobalSearchItemType,
  EGlobalSearchTab,
  EGlobalSearchTabBrowse,
  type IGlobalSearchGroup,
  type IGlobalSearchStaticEntry,
} from "@frontend-shared/types";
import {
  filterGlobalSearchGroupsByTab,
  getGlobalSearchTabDefinition,
  isGlobalSearchTabShowingType,
  listGlobalSearchFaqItems,
  listGlobalSearchIntegrationProviderItems,
  listGlobalSearchProjectItems,
  listGlobalSearchStaticEntries,
  sortGlobalSearchGroups,
} from "@frontend-shared/utils";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGlobalSearchBrowse } from "@/api/global-search/useGlobalSearchBrowse";
import { useAuth } from "@/hooks/use-auth";
import { useFaqData } from "@/pages/faq/hooks/use-faq-data";

import type {
  IGlobalSearchServerGroupsResult,
  IUseGlobalSearchBrowseGroupsProps,
} from "../types";
import { useGlobalSearchGateResolver } from "./use-global-search-gate-resolver";
import { useGlobalSearchIntegrationProviders } from "./use-global-search-integration-providers";
import { useGlobalSearchTranslate } from "./use-global-search-translate";

const EMPTY_GROUPS: IGlobalSearchGroup[] = [];

const BROWSE_STATIC_ENTRIES_BY_TYPE: ReadonlyArray<
  readonly [EGlobalSearchItemType, readonly IGlobalSearchStaticEntry[]]
> = GLOBAL_SEARCH_BROWSE_STATIC_TYPES.map((type) => [
  type,
  GLOBAL_SEARCH_STATIC_ENTRIES.filter((entry) => entry.type === type),
]);

// NOTE: NSAI-6853 — what a category tab lists before anything is typed, so
// every tab opens on something, as All and Chats open on the recent chats:
// - built on the device: the pages / settings tabs of the static index, the
//   projects to switch to, the FAQ and the connector catalog;
// - from GET /search/global?browse=true: every server source's default
//   listing (assistants, models, agents, files, skills, …), with the same
//   items, deep links and icons a search returns.
// Everything is narrowed to the active tab. All browses chats only, so it
// never sends the browse request.
export const useGlobalSearchBrowseGroups = ({
  tab,
  isEnabled,
}: IUseGlobalSearchBrowseGroupsProps): IGlobalSearchServerGroupsResult => {
  const { t } = useTranslation("globalSearch");
  const { projects, currentProjectUuid } = useAuth();
  const translate = useGlobalSearchTranslate();
  const isGateOpen = useGlobalSearchGateResolver({ isEnabled });
  const { browse, hasServerResults } = getGlobalSearchTabDefinition(tab);
  const isCategoryTab = isEnabled && tab !== EGlobalSearchTab.ALL;
  // NOTE: A tab that browses the recent chats (Chats) has them already, so it
  // never pays for the browse fan-out.
  const isServerBrowsed =
    isCategoryTab &&
    hasServerResults &&
    browse === EGlobalSearchTabBrowse.ITEMS;
  const hasFaq =
    isCategoryTab &&
    isGlobalSearchTabShowingType({ tab, type: EGlobalSearchItemType.FAQ_ITEM });
  const hasProviders =
    isCategoryTab &&
    isGlobalSearchTabShowingType({
      tab,
      type: EGlobalSearchItemType.INTEGRATION_PROVIDER,
    }) &&
    isGateOpen(EGlobalSearchGate.INTEGRATION_READ);
  const { data: faqCategories } = useFaqData({ isEnabled: hasFaq });
  const integrationProviders = useGlobalSearchIntegrationProviders({
    isEnabled: hasProviders,
    translate,
  });
  const {
    data: serverData,
    isLoading,
    isError,
    refetch,
  } = useGlobalSearchBrowse({ enabled: isServerBrowsed });

  const groups = useMemo(() => {
    if (!isCategoryTab) return EMPTY_GROUPS;

    const staticGroups = BROWSE_STATIC_ENTRIES_BY_TYPE.map(
      ([type, entries]) => ({
        type,
        items: listGlobalSearchStaticEntries({
          entries,
          translate,
          isGateOpen,
        }),
      }),
    );
    const clientGroups: IGlobalSearchGroup[] = [
      ...staticGroups,
      {
        type: EGlobalSearchItemType.PROJECT,
        items: listGlobalSearchProjectItems({
          projects,
          currentProjectUuid,
          personalLabel: t("badges.personal"),
        }),
      },
      {
        type: EGlobalSearchItemType.INTEGRATION_PROVIDER,
        items: hasProviders
          ? listGlobalSearchIntegrationProviderItems({
              providers: integrationProviders,
              limit: GLOBAL_SEARCH_BROWSE_LIMIT,
            })
          : [],
      },
      {
        type: EGlobalSearchItemType.FAQ_ITEM,
        items: listGlobalSearchFaqItems({
          categories: faqCategories ?? [],
          limit: GLOBAL_SEARCH_BROWSE_LIMIT,
        }),
      },
    ];

    // NOTE: Capped per group AFTER the tab filter — every browse list, built
    // here or on the server, shows the same few items, and profile entries
    // never use up the Org settings allowance. Typing finds the rest.
    return filterGlobalSearchGroupsByTab({
      tab,
      groups: sortGlobalSearchGroups([
        ...clientGroups,
        ...(serverData?.groups ?? []),
      ]),
    }).map((group) => ({
      type: group.type,
      items: group.items.slice(0, GLOBAL_SEARCH_BROWSE_LIMIT),
    }));
  }, [
    isCategoryTab,
    tab,
    translate,
    isGateOpen,
    projects,
    currentProjectUuid,
    t,
    hasProviders,
    integrationProviders,
    faqCategories,
    serverData,
  ]);

  const handleRetry = useCallback((): void => {
    void refetch();
  }, [refetch]);

  return {
    groups,
    isLoading: isServerBrowsed && isLoading,
    isError: isServerBrowsed && isError,
    onRetry: handleRetry,
  };
};
